import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import { useSettings } from '../context/SettingsContext';
import {
  FaArrowRight,
  FaShippingFast,
  FaShieldAlt,
  FaHeadset,
  FaUndoAlt,
  FaStar,
} from 'react-icons/fa';
import './Home.css';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [latestProducts, setLatestProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();

  useEffect(() => {
    Promise.all([loadFeatured(), loadLatest(), loadCategories()])
      .finally(() => setLoading(false));
  }, []);

  const loadFeatured = async () => {
    try {
      const res = await axios.get('/api/products?featured=true&limit=4');
      setFeaturedProducts(res.data.products);
    } catch (e) { console.error(e); }
  };

  const loadLatest = async () => {
    try {
      const res = await axios.get('/api/products?limit=8&sort=-createdAt');
      setLatestProducts(res.data.products);
    } catch (e) { console.error(e); }
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get('/api/categories');
      setCategories(res.data.slice(0, 6));
    } catch (e) { console.error(e); }
  };

  const ProductCard = ({ product }) => {
    const discount = product.comparePrice && product.comparePrice > product.price
      ? Math.round(((product.comparePrice - product.price) / product.comparePrice) * 100)
      : 0;

    return (
      <Link to={`/products/${product._id}`} className="product-card">
        <div className="product-image">
          {product.images && product.images[0] ? (
            <img src={product.images[0].url} alt={product.name} loading="lazy" />
          ) : (
            <div className="no-image">No Image</div>
          )}
          {discount > 0 && <span className="product-discount">-{discount}%</span>}
          {product.featured && <span className="product-badge">Featured</span>}
        </div>
        <div className="product-info">
          <h3>{product.name}</h3>
          {product.rating?.average > 0 && (
            <div className="product-rating">
              <FaStar /> {product.rating.average.toFixed(1)}
              <span>({product.rating.count})</span>
            </div>
          )}
          <div className="product-price-row">
            <span className="product-price">{formatCurrency(product.price, settings?.currency)}</span>
            {product.comparePrice > product.price && (
              <span className="product-compare">{formatCurrency(product.comparePrice, settings?.currency)}</span>
            )}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero">
        <div className="container hero-grid">
          <div className="hero-content">
            <span className="hero-eyebrow">{settings?.heroEyebrow || 'Centre Techniques · Premium Instruments'}</span>
            <h1>{settings?.heroTitle || 'Where music comes to life'}</h1>
            <p>
              {settings?.heroSubtitle ||
                'Premium pianos, professional instruments, and studio gear hand-picked for serious musicians. Tuned, tested, and trusted by performers, teachers, and conservatories.'}
            </p>
            <div className="hero-cta">
              <Link to="/products" className="btn btn-primary btn-lg">
                Explore catalogue <FaArrowRight />
              </Link>
              <Link to="/products?category=pianos" className="btn btn-secondary btn-lg">
                View pianos
              </Link>
            </div>
            <div className="hero-stats">
              <div><strong>500+</strong><span>Instruments</span></div>
              <div><strong>30+ yrs</strong><span>Of expertise</span></div>
              <div><strong>4.9★</strong><span>Customer rating</span></div>
            </div>
          </div>

          <div className="hero-visual">
            {settings?.heroImage ? (
              <div className="hero-banner-img">
                <img src={settings.heroImage} alt={settings?.siteName || 'Centre Techniques Pianos'} />
              </div>
            ) : (
              <>
                <div className="hero-card hero-card-1">
                  <div className="hero-card-img" style={{ background: 'linear-gradient(135deg,#a3d96a,#5ea832)' }} />
                  <div>
                    <strong>Free shipping</strong>
                    <small>On orders over {formatCurrency(settings?.freeShippingThreshold || 50000, settings?.currency)}</small>
                  </div>
                </div>
                <div className="hero-card hero-card-2">
                  <div className="hero-card-img" style={{ background: 'linear-gradient(135deg,#1f6f3a,#0a2308)' }} />
                  <div>
                    <strong>Top rated</strong>
                    <small>From verified buyers</small>
                  </div>
                </div>
                <div className="hero-blob" />
              </>
            )}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features">
        <div className="container features-grid">
          <div className="feature">
            <FaShippingFast /><div><h4>Fast delivery</h4><p>Next-day options available</p></div>
          </div>
          <div className="feature">
            <FaShieldAlt /><div><h4>Secure payments</h4><p>SSL encrypted checkout</p></div>
          </div>
          <div className="feature">
            <FaUndoAlt /><div><h4>Easy returns</h4><p>30-day return window</p></div>
          </div>
          <div className="feature">
            <FaHeadset /><div><h4>24/7 support</h4><p>We're always here to help</p></div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      {categories.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section-header">
              <div>
                <h2>Shop by category</h2>
                <p>Browse instruments and gear by department</p>
              </div>
            </div>
            <div className="categories-grid">
              {categories.map((cat) => (
                <Link
                  key={cat._id}
                  to={`/products?category=${cat._id}`}
                  className="category-card"
                >
                  {cat.image ? (
                    <img src={cat.image} alt={cat.name} />
                  ) : (
                    <div className="category-fallback">
                      {cat.name.charAt(0)}
                    </div>
                  )}
                  <span>{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED */}
      {featuredProducts.length > 0 && (
        <section className="section featured-section">
          <div className="container">
            <div className="section-header">
              <div>
                <h2>Featured instruments</h2>
                <p>Hand-picked premium picks from our experts</p>
              </div>
              <Link to="/products?featured=true" className="section-link">
                View all <FaArrowRight />
              </Link>
            </div>
            <div className="products-grid">
              {featuredProducts.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LATEST */}
      {latestProducts.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section-header">
              <div>
                <h2>Latest arrivals</h2>
                <p>Fresh additions to our showroom</p>
              </div>
              <Link to="/products" className="section-link">
                Browse all <FaArrowRight />
              </Link>
            </div>
            <div className="products-grid">
              {latestProducts.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {!loading && featuredProducts.length === 0 && latestProducts.length === 0 && (
        <section className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
          <h2>No products yet</h2>
          <p style={{ color: 'var(--gray-500)', margin: '12px 0 24px' }}>
            Check back soon — we're stocking the shelves.
          </p>
          <Link to="/products" className="btn btn-primary">Browse store</Link>
        </section>
      )}

      {/* CTA */}
      <section className="cta">
        <div className="container cta-card">
          <div>
            <h2>Ready to find your sound?</h2>
            <p>Join the musicians, teachers, and studios who trust {settings?.siteName || 'Centre Techniques Pianos'} for their instruments and gear.</p>
          </div>
          <Link to="/register" className="btn btn-primary btn-lg">
            Create account <FaArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
